import Vue from "vue"
import store from "@/store"
import routes from "./routes"
import Router from "vue-router"
import { sync } from "vuex-router-sync"
import GTMAPI from "@/integration/GTMAPI"

import { parseStoreConfigFromUrl } from "@/utils"
import { waitForScrollEnd } from "../utils"
// import EdgeFunctions from "@/integration/Netlify"

let router = null

Vue.use(Router)

// The middleware for every page of the application.
const globalMiddleware = [
  "check-auth",
  "check-loaded",
  /* "auth-redirect", */
  "content-images",
  "split-test-override",
  "newsletter-modal",
  "cf-source"
]

// Load middleware modules dynamically.
const routeMiddleware = resolveMiddleware(
  require.context("@/middleware", false, /.*\.js$/)
)

export async function createRouterInstance() {
  // Wait for async store config
  const {
    countryCode,
    countryPath,
    languagePath,
    language
  } = await parseStoreConfigFromUrl(
    window.location.pathname,
    store.state.locale.localeOptions
  )

  Bleach.debug.log(
    "ROUTER CREATE",
    countryCode,
    "|   countryPath:",
    countryPath,
    "|   languagePath:",
    languagePath,
    "lang:",
    language
  )

  router = new Router({
    base: `${countryPath ? "/" + countryPath : ""}${
      languagePath ? "/" + languagePath : ""
    }`,
    scrollBehavior: (to, from, savedPosition) =>
      scrollBehavior(router, to, from, savedPosition),
    mode: "history",
    routes
  })

  router.beforeEach((to, from, next) => beforeEach(router, to, from, next))
  router.afterEach((to, from) => afterEach(router, to, from))

  // Sync Vuex
  sync(store, router)

  // Patch router.push to avoid duplicate navigation errors
  const { isNavigationFailure, NavigationFailureType } = Router
  const originalRouterPush = Router.prototype.push
  Router.prototype.push = function push(target) {
    return originalRouterPush.call(this, target).catch(err => {
      if (!isNavigationFailure(err, NavigationFailureType.duplicated)) {
        throw err
      } else {
        store.commit("ui/SET_ROUTE_TRANSITION_STATE", false)
      }
    })
  }

  Bleach.debug.log("ROUTER CREATED!", router)

  return router
}

/**
 * Global router guard.
 *
 * @param {Route} to
 * @param {Route} from
 * @param {Function} next
 */
async function beforeEach(router, to, from, next) {
  // store.commit("ui/SET_ROUTE_TRANSITION_STATE", true)

  let components = []

  try {
    // Get the matched components and resolve them.
    components = await resolveComponents(router.getMatchedComponents({ ...to }))
  } catch (error) {
    if (/^Loading( CSS)? chunk (\d)+ failed\./.test(error.message)) {
      window.location.reload(true)
      return
    }
  }

  if (components.length === 0) {
    return next()
  }

  // Start the loading bar.
  // if (components[components.length - 1].loading !== false) {
  //   router.app.$nextTick(() => router.app.$loading.start())
  // }

  // Get the middleware for all the matched components.
  const middleware = getMiddleware(components)

  // Call each middleware.
  callMiddleware(middleware, to, from, (...args) => {
    // Set the application layout only if "next()" was called with no args.
    // if (args.length === 0) {
    //   router.app.setLayout(components[0].layout || "")
    // }

    next(...args)
  })
}

/**
 * Global after hook.
 *
 * @param {Route} to
 * @param {Route} from
 * @param {Function} next
 */
async function afterEach(router, to) {
  await router.app.$nextTick()
  const buuid = store.state.identity.buuid
  const email =
    store.state.identity.userDetails && store.state.identity.userDetails.email
  const locale = store.state.localeConfig?.countryCode
  store.commit("ui/SET_ROUTE_TRANSITION_STATE", false)
  Bleach.debug.log("AFTER EACH!", to)
  GTMAPI.trackEvent("pageview", { buuid, email, locale })

  //router.app.$loading.finish()
}

/**
 * Call each middleware.
 *
 * @param {Array} middleware
 * @param {Route} to
 * @param {Route} from
 * @param {Function} next
 */
function callMiddleware(middleware, to, from, next) {
  const stack = middleware.reverse()

  const _next = (...args) => {
    // Stop if "_next" was called with an argument or the stack is empty.
    if (args.length > 0 || stack.length === 0) {
      if (args.length > 0) {
        //router.app.$loading.finish()
      }

      return next(...args)
    }

    const middleware = stack.pop()

    if (typeof middleware === "function") {
      middleware(to, from, _next)
    } else if (routeMiddleware[middleware]) {
      routeMiddleware[middleware](to, from, _next)
    } else {
      throw Error(`Undefined middleware [${middleware}]`)
    }
  }

  _next()
}

/**
 * Resolve async components.
 *
 * @param  {Array} components
 * @return {Array}
 */
function resolveComponents(components) {
  return Promise.all(
    components.map(component => {
      return typeof component === "function" ? component() : component
    })
  )
}

/**
 * Merge the the global middleware with the components middleware.
 *
 * @param  {Array} components
 * @return {Array}
 */
function getMiddleware(components) {
  const middleware = [...globalMiddleware]

  components
    .filter(c => c.middleware)
    .forEach(component => {
      if (Array.isArray(component.middleware)) {
        middleware.push(...component.middleware)
      } else {
        middleware.push(component.middleware)
      }
    })

  return middleware
}

/**
 * Scroll Behavior
 *
 * @link https://router.vuejs.org/en/advanced/scroll-behavior.html
 *
 * @param  {Route} to
 * @param  {Route} from
 * @param  {Object|undefined} savedPosition
 * @return {Object}
 */
function scrollBehavior(router, to, from, savedPosition) {
  if (savedPosition) {
    return savedPosition
  }

  if (to.params.maintainScrollPos) return {}

  if (to.hash) {
    const hash = to.hash
    const intervalMs = 50 // polling frequency
    const timeoutMs = 2000 // max wait time (2s)

    return new Promise(resolve => {
      const start = Date.now()

      const attempt = () => {
        const el = document.querySelector(hash)

        // console.log("ATTEMPT", el)

        if (el) {
          const rect = el.getBoundingClientRect()

          if (rect.height > 0) {
            requestAnimationFrame(async () => {
              clearInterval(intervalId)

              store.commit("scroll/SET_AUTOSCROLL", true)

              const offset =
                parseFloat(getComputedStyle(el).scrollMarginTop) || 0

              // console.log("OFFSET", offset)

              resolve({
                selector: hash,
                offset: { y: offset },
                behavior: "smooth"
              })

              await waitForScrollEnd()

              store.commit("scroll/SET_AUTOSCROLL", false)
            })
            return
          }
        }

        if (Date.now() - start >= timeoutMs) {
          clearInterval(intervalId)

          // Graceful fallback — do nothing
          resolve(false)
        }
      }

      const intervalId = setInterval(attempt, intervalMs)

      // Try immediately (avoid initial 50ms delay)
      attempt()
    })
  }

  const [component] = router.getMatchedComponents({ ...to }).slice(-1)

  if (component && component.scrollToTop === false) {
    return {}
  }

  return { x: 0, y: 0 }
}

/**
 * @param  {Object} requireContext
 * @return {Object}
 */
function resolveMiddleware(requireContext) {
  return requireContext
    .keys()
    .map(file => [file.replace(/(^.\/)|(\.js$)/g, ""), requireContext(file)])
    .reduce(
      (guards, [name, guard]) => ({ ...guards, [name]: guard.default }),
      {}
    )
}

export function getRouter() {
  if (!router) {
    throw new Error(
      "Router not yet created! Call createRouterInstance() first."
    )
  }
  return router
}
